// Atlas Beasts Kenney Asset Manifest
// Temporary placeholder assets — replaced by original art when ready
// All assets CC0 licensed — kenney.nl

export const KENNEY_BASE = '/assets/kenney'
export const RENDERED_BASE = '/assets/kenney/rendered'

// Nature-kit isometric PNGs — immediately usable (no render step needed)
const NK = `${KENNEY_BASE}/zones/nature-kit/Isometric`
// Space-kit isometric PNGs — immediately usable
const SK = `${KENNEY_BASE}/zones/modular-space-kit/Isometric`

// Character spritesheet (roguelike-characters pack — 2D, immediately usable)
export const CHARACTER_SPRITESHEET = `${KENNEY_BASE}/characters/animated-protagonists/Spritesheet/roguelikeChar_transparent.png`

// Character pack assignments per Atlas Beasts character
// All 3D packs (animated-protagonists, animated-retro, animated-survivors) need
// pre-rendering before they can be used — paths point to rendered/ output folder
export const CHARACTER_ASSETS = {
  'kai-voss':          `${RENDERED_BASE}/characters/animated-protagonists/cyborg.png`,
  'sela-mourne':       `${RENDERED_BASE}/characters/animated-retro/dark-01.png`,
  'drego':             `${RENDERED_BASE}/characters/animated-survivors/large-01.png`,
  'vara-six':          `${RENDERED_BASE}/characters/animated-protagonists/skater.png`,
  'marshal-coen':      `${RENDERED_BASE}/characters/animated-survivors/soldier.png`,
  'lune':              `${RENDERED_BASE}/characters/animated-retro/mystic.png`,
  'reef':              `${RENDERED_BASE}/characters/animated-protagonists/slim-01.png`,
  'oska-unmade':       `${RENDERED_BASE}/characters/animated-retro/dark-02.png`,
  'twin-daggers-yara': `${RENDERED_BASE}/characters/animated-protagonists/criminal.png`,
} as const

// Creature pack assignments per Atlas Beasts creature
// All point to rendered/ — nature-kit has no animal sprites, only environment tiles
export const CREATURE_ASSETS = {
  'rell':            `${RENDERED_BASE}/zones/nature-kit/wolf.png`,
  'crag':            `${RENDERED_BASE}/zones/nature-kit/bear.png`,
  'flick':           `${RENDERED_BASE}/zones/nature-kit/bird.png`,
  'warden':          `${RENDERED_BASE}/zones/nature-kit/wolf-armored.png`,
  'orrin':           `${RENDERED_BASE}/zones/nature-kit/fish.png`,
  'null':            `${RENDERED_BASE}/zones/graveyard-kit/monster-01.png`,
  'mist':            `${RENDERED_BASE}/zones/graveyard-kit/ghost.png`,
  'scorch':          `${RENDERED_BASE}/zones/nature-kit/lizard.png`,
  'origin-founding': `${RENDERED_BASE}/zones/modular-space-kit/orb.png`,
} as const

// Zone environment decorative tiles — isometric PNGs, usable NOW
// Each zone gets a curated set of 4 tiles for background atmosphere
export const ZONE_ENV_TILES = {
  ashwake: [
    `${NK}/campfire_bricks_NE.png`,
    `${NK}/cliff_block_rock_NE.png`,
    `${NK}/cliff_blockCave_rock_NE.png`,
    `${NK}/bridge_stone_NE.png`,
  ],
  rootlands: [
    `${NK}/campfire_logs_NE.png`,
    `${NK}/cliff_block_stone_NE.png`,
    `${NK}/bridge_center_stone_NE.png`,
    `${NK}/bridge_stoneRound_NE.png`,
  ],
  glassdeep: [
    `${SK}/corridor_NE.png`,
    `${SK}/corridor_cornerRound_NE.png`,
    `${SK}/barrel_NE.png`,
    `${SK}/corridor_roof_NE.png`,
  ],
  skyrift: [
    `${SK}/corridor_open_NE.png`,
    `${SK}/corridor_detailed_NE.png`,
    `${SK}/chimney_NE.png`,
    `${SK}/corridor_cross_NE.png`,
  ],
  'null-current': [
    `${SK}/bones_NE.png`,
    `${SK}/corridor_wall_NE.png`,
    `${SK}/barrels_NE.png`,
    `${SK}/corridor_end_NE.png`,
  ],
  convergence: [
    `${NK}/campfire_stones_NE.png`,
    `${SK}/astronautA_NE.png`,
    `${NK}/bridge_center_stoneRound_NE.png`,
    `${SK}/corridor_corner_NE.png`,
  ],
} as const

// Zone asset packs — lazy loaded per zone
export const ZONE_ASSETS = {
  ashwake: {
    primary:   `${KENNEY_BASE}/zones/city-industrial/`,
    secondary: `${KENNEY_BASE}/zones/train-kit/`,
    encounter: `${KENNEY_BASE}/encounters/mini-arena/`,
    tiles: ZONE_ENV_TILES.ashwake,
    tint: { filter: 'sepia(0.4) hue-rotate(340deg) saturate(2)' },
    color: '#FF4800',
  },
  rootlands: {
    primary:   `${KENNEY_BASE}/zones/nature-kit/`,
    secondary: `${KENNEY_BASE}/zones/survival-kit/`,
    encounter: `${KENNEY_BASE}/encounters/mini-dungeon/`,
    tiles: ZONE_ENV_TILES.rootlands,
    tint: { filter: 'sepia(0.3) hue-rotate(70deg) saturate(3)' },
    color: '#39FF14',
  },
  glassdeep: {
    primary:   `${KENNEY_BASE}/zones/modular-space-kit/`,
    secondary: `${KENNEY_BASE}/zones/nature-kit/`,
    encounter: `${KENNEY_BASE}/encounters/mini-arena/`,
    tiles: ZONE_ENV_TILES.glassdeep,
    tint: { filter: 'sepia(0.3) hue-rotate(160deg) saturate(2)' },
    color: '#00F0FF',
  },
  skyrift: {
    primary:   `${KENNEY_BASE}/zones/platformer-kit/`,
    secondary: `${KENNEY_BASE}/zones/modular-space-kit/`,
    encounter: `${KENNEY_BASE}/encounters/mini-arena/`,
    tiles: ZONE_ENV_TILES.skyrift,
    tint: { filter: 'brightness(1.5) saturate(0.4)' },
    color: '#B8EEFF',
  },
  'null-current': {
    primary:   `${KENNEY_BASE}/zones/graveyard-kit/`,
    secondary: `${KENNEY_BASE}/zones/space-station-kit/`,
    encounter: `${KENNEY_BASE}/encounters/mini-dungeon/`,
    tiles: ZONE_ENV_TILES['null-current'],
    tint: { filter: 'grayscale(1) brightness(0.3)' },
    color: '#BF00FF',
  },
  convergence: {
    primary:   `${KENNEY_BASE}/zones/nature-kit/`,
    secondary: `${KENNEY_BASE}/zones/modular-space-kit/`,
    encounter: `${KENNEY_BASE}/encounters/mini-arena/`,
    tiles: ZONE_ENV_TILES.convergence,
    tint: { filter: 'none' },
    color: '#FFE800',
  },
} as const

export const UI_ASSETS = {
  buttons:    `${KENNEY_BASE}/ui/ui-pack-sci-fi/`,
  panels:     `${KENNEY_BASE}/ui/ui-pack-sci-fi/`,
  hpBars:     `${KENNEY_BASE}/ui/ui-pack-rpg/`,
  icons:      `${KENNEY_BASE}/ui/game-icons/`,
  iconsExtra: `${KENNEY_BASE}/ui/game-icons-expansion/`,
  patterns:   `${KENNEY_BASE}/textures/pattern-pack/`,
} as const

export const WEAPON_ASSETS = {
  base: `${KENNEY_BASE}/weapons/blaster-kit/`,
} as const

export type ZoneId = keyof typeof ZONE_ASSETS
export type CharacterId = keyof typeof CHARACTER_ASSETS
export type CreatureId = keyof typeof CREATURE_ASSETS
